import Permissions from 'javascripts/analytics/config/permissions';
import _ from 'underscore';
import moment from 'moment';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { CountryFlag } from 'javascripts/analytics/components/country_flag';
import { MoreButton } from 'javascripts/analytics/components/more_button';
import { PremiumIcon } from 'javascripts/analytics/components/premium_icon';
import { capitalizeCountryName } from 'javascripts/analytics/utils/country_names';
import {
  trackAnalyticsClick,
  watchAnalyticsImpression,
} from 'js/analytics/metrics/analyticsTracking';

export const PremiumHitsTable = withRouter(
  class extends React.Component {
    static propTypes = {
      hits: PropTypes.array.isRequired,
      location: PropTypes.object.isRequired,
    };

    state = {
      numVisibleRows: 10,
    };

    impressionRefs = new Map();

    componentWillUnmount() {
      this.impressionRefs.forEach((ref) => ref(null));
    }

    shouldRestrictPremiumData = () => {
      if (Permissions.inMode('premium')) {
        return false;
      } else {
        return true;
      }
    };

    chronologicalHits = () => {
      return _.sortBy(this.props.hits, function (hit) {
        // Sort by date, show most recent first
        return -1 * hit.epoch;
      });
    };

    visibleHits = () => {
      return this.chronologicalHits().slice(0, this.state.numVisibleRows);
    };

    hitOccupation = (hit) => {
      return (
        hit.visitor && hit.visitor.occupation && hit.visitor.occupation.name
      );
    };

    hitUniversityName = (hit) => {
      return (
        hit.visitor &&
        hit.visitor.university &&
        hit.visitor.university.display_name
      );
    };

    hitDisplayCity = (hit) => {
      return _.compact([hit.visitor.city, hit.visitor.region]).join(', ');
    };

    hitDisplayCountry = (hit) => {
      return capitalizeCountryName(hit.visitor.country.name);
    };

    renderVisitorRole = (hit) => {
      if (!this.hitOccupation(hit)) return;
      return <div>{this.hitOccupation(hit)}</div>;
    };

    renderVisitorUniversity = (hit) => {
      if (!this.hitUniversityName(hit)) return;

      if (this.shouldRestrictPremiumData()) {
        return this.hitUniversityName(hit);
      } else {
        return (
          <a
            className="u-tcGrayDarker"
            href={hit.visitor.university.url}
            onClick={() =>
              trackAnalyticsClick(
                'analytics-content-link',
                { link_type: 'university', table: 'premium_hits' },
                Number.isInteger(hit.visitor.university.id)
                  ? hit.visitor.university.id
                  : undefined,
              )
            }
          >
            {this.hitUniversityName(hit)}
          </a>
        );
      }
    };

    setVisitor = (hit) => {
      this.setState({
        hoverVisitor: hit.visitor.id,
      });
    };

    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    clearVisitor = (hit) => {
      this.setState({
        hoverVisitor: false,
      });
    };

    readerPreviews = (hits, header) => {
      const readerHits = _.filter(hits, function (hit) {
        return (
          hit.page &&
          // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
          // eslint-disable-next-line eqeqeq
          hit.page.category == 'Document' &&
          hit.visitor &&
          hit.visitor.country &&
          hit.visitor.country.code &&
          hit.visitor.country.name
        );
      });

      const readerHitsByVisitor = _.groupBy(readerHits, function (hit) {
        return hit.visitor.id;
      });

      const previews = _.map(
        readerHitsByVisitor,
        function (hits) {
          const visitor = hits[0].visitor;
          let otherPapers = '';
          if (hits.length > 1) {
            otherPapers = $h.numberAndNoun(hits.length - 1, 'other paper');
          }
          const upgradeUrl = $r.upgrade_url({
            feature: 'readers',
            trigger: 'analytics-hits-reader-preview',
          });
          return (
            <tr
              key={'reader_' + visitor.id.toString()}
              className="premium-hits-table-tr"
            >
              <td colSpan={header.length} className="u-p2x">
                <a
                  className="Button Button--inverseGreen Button--xs u-floatRight u-mr4x"
                  style={{ width: 135 }}
                  href={upgradeUrl}
                  ref={this.doorImpressionRef(
                    `hits_reader_preview:${visitor.id}`,
                    'hits_reader_preview',
                    'analytics-hits-reader-preview',
                  )}
                  onClick={() =>
                    this.trackUpgradeDoorClick(
                      'hits_reader_preview',
                      'analytics-hits-reader-preview',
                      {},
                      hits[0].page.work_id,
                    )
                  }
                >
                  UPGRADE TO VIEW
                </a>
                <div
                  className="u-textTruncate u-letterSpacing0_2 u-ph2x"
                  style={{ width: 'calc(100% - 195px)', lineHeight: '28px' }}
                >
                  <span className="u-ph2x">
                    <CountryFlag country={visitor.country} />
                  </span>
                  Someone from <strong>{visitor.country.name}</strong> read{' '}
                  <strong>{hits[0].page.title}</strong>
                  {otherPapers && ' and '}
                  <strong>{otherPapers}</strong>
                </div>
              </td>
            </tr>
          );
        }.bind(this),
      );
      return previews;
    };

    renderRows = (header) => {
      const hits = this.visibleHits();
      let readerPreviews = [];
      if (!this.userIsPremium()) {
        readerPreviews = this.readerPreviews(this.chronologicalHits(), header);
      }
      const rows = [];
      let readerIndex = 0;
      for (let hitIndex = 0; hitIndex < hits.length; hitIndex++) {
        rows.push(this.renderRow(hits[hitIndex]));
        if ((hitIndex + 1) % 3 === 0 && readerIndex < readerPreviews.length) {
          rows.push(readerPreviews[readerIndex++]);
        }
      }
      return <tbody className="premium-hits-table-tbody">{rows}</tbody>;
    };

    renderRow = (hit) => {
      // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
      // eslint-disable-next-line eqeqeq
      const isHoverVisitor = hit.visitor.id == this.state.hoverVisitor;
      const sourceContentArr = (hit.page.title || hit.page.category).split(' ');

      return (
        <tr
          className={
            isHoverVisitor
              ? 'u-bgColorBlueLightest premium-hits-table-tr'
              : 'premium-hits-table-tr'
          }
          onMouseEnter={this.setVisitor.bind(this, hit)}
          onMouseLeave={this.clearVisitor}
          key={hit.id}
        >
          <td className="premium-hits-table-td">
            {moment(hit.epoch).format('H:mm MMM D')}
          </td>
          {(this.shouldRestrictPremiumData()
            ? this.lockedCell
            : this.unlockedCell)(
            this.renderVisitorRole(hit),
            'role',
            hit.page.work_id,
            hit.id,
          )}
          {(this.shouldRestrictPremiumData()
            ? this.lockedCell
            : this.unlockedCell)(
            this.renderVisitorUniversity(hit),
            'university',
            hit.page.work_id,
            hit.id,
          )}
          <td className="premium-hits-table-td">{this.hitDisplayCity(hit)}</td>
          <td className="premium-hits-table-td">
            {this.hitDisplayCountry(hit)}
          </td>
          <td className="premium-hits-table-td">
            <a
              className="u-tcGrayDarker"
              href={hit.page.url}
              onClick={() =>
                trackAnalyticsClick(
                  'analytics-content-link',
                  { link_type: 'page', table: 'premium_hits' },
                  Number.isInteger(hit.page.work_id)
                    ? hit.page.work_id
                    : undefined,
                )
              }
            >
              {sourceContentArr.length > 4
                ? sourceContentArr.slice(0, 5).join(' ') + '...'
                : sourceContentArr.join(' ')}
            </a>
          </td>
          <td className="premium-hits-table-td">
            {hit.referer && hit.referer.url ? (
              <a
                className="u-tcGrayDarker"
                href={hit.referer.url}
                onClick={() =>
                  trackAnalyticsClick('analytics-content-link', {
                    link_type: 'referrer',
                    table: 'premium_hits',
                  })
                }
              >
                {hit.referer.domain}
              </a>
            ) : null}
          </td>
          {(this.shouldRestrictPremiumData()
            ? this.lockedCell
            : this.unlockedCell)(
            hit.furthest_page,
            'pages',
            hit.page.work_id,
            hit.id,
          )}
        </tr>
      );
    };

    unlockedCell = (data) => {
      return <td className="premium-hits-table-td">{data}</td>;
    };

    lockedCell = (data, name, entityId, rowKey) => {
      const trigger = name + '-cell';
      return (
        <td
          className="premium-hits-table-td premium-hits-table-restricted-data u-clickable u-userSelectNone"
          ref={this.doorImpressionRef(
            `hits_locked_cell:${name}:${rowKey}`,
            'hits_locked_cell',
            trigger,
            { column: name },
          )}
          onClick={() =>
            this.handleUpgradeClick(
              trigger,
              'hits_locked_cell',
              { column: name },
              entityId,
            )
          }
        >
          {data}
        </td>
      );
    };

    moreButtonClickHandler = () => {
      this.setState({
        numVisibleRows: this.state.numVisibleRows + 20,
      });
    };

    renderMoreButton = (isVisible) => {
      if (isVisible) {
        return (
          <MoreButton
            onClick={this.moreButtonClickHandler}
            trackingTable="premium_hits"
          />
        );
      }
      return null;
    };

    render() {
      // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
      // eslint-disable-next-line eqeqeq
      if (this.props.hits.length == 0) return null;

      const header = React.Children.toArray([
        <th className="premium-hits-table-th" style={{ width: '10%' }}>
          Date
        </th>,
        this.premiumHeader(
          'Role',
          'role',
          'See the roles of your readers to learn more about who is reading your work.',
        ),
        this.premiumHeader(
          'University',
          'university',
          'Find out which universities your readers are from.',
        ),
        <th className="premium-hits-table-th">City & State</th>,
        <th className="premium-hits-table-th">Country</th>,
        <th className="premium-hits-table-th">Content</th>,
        <th className="premium-hits-table-th">Source</th>,
        this.premiumHeader(
          'Pages Read',
          'pages',
          'Find out how many pages are being read for each paper.',
        ),
      ]);

      return (
        <div
          className="panel panel-flat a-fadeInDown"
          style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
        >
          <div className="u-p5x u-tcGrayDarker">User Activity</div>
          <div className="panel-horizontal-split"></div>
          <div>
            <table
              className="table u-mb0x analytics-dashboard-data-table"
              style={{ tableLayout: 'auto' }}
            >
              <thead className="premium-hits-table-thead">
                <tr>{header}</tr>
              </thead>
              {this.renderRows(header)}
            </table>
          </div>
          <div className="panel-horizontal-split"></div>
          <div className="u-taCenter">
            {this.renderMoreButton(
              this.props.hits.length > this.state.numVisibleRows,
            )}
          </div>
        </div>
      );
    }

    premiumIcon = (data) => {
      if (!this.props.location.pathname.includes('/activity/overview'))
        return null;

      const trigger = data.name + '-header';
      const doorExtra = { column: data.name };

      return (
        <span>
          <PremiumIcon
            style={{ top: '-1px' }}
            className="u-positionRelative"
            popoverBodyText={data.popoverBodyText}
            popoverLinkUrl={$r.upgrade_url({
              feature: 'academia_analytics_expanded',
              trigger: trigger,
            })}
            popoverContainer="body"
            disableHoverBehavior={this.userIsPremium()}
            // HoverPopover swallows the <th>'s React onClick on Overview, so
            // track here. Track-only: this header never navigated on Overview.
            onTriggerClick={() =>
              this.trackUpgradeDoorClick(
                'hits_locked_header',
                trigger,
                doorExtra,
              )
            }
            onPopoverLinkClick={() =>
              this.trackUpgradeDoorClick(
                'hits_locked_header',
                trigger,
                doorExtra,
              )
            }
          />
          &nbsp;&nbsp;
        </span>
      );
    };

    premiumHeader = (title, name, popoverBodyText) => {
      const classes = classNames(
        'premium-hits-table-th js-PremiumIconContainerHoverWrapper PremiumIconContainerHoverWrapper',
        {
          'u-clickable': !this.userIsPremium(),
        },
      );

      return (
        <th
          className={classes}
          ref={
            this.userIsPremium()
              ? undefined
              : this.doorImpressionRef(
                  `hits_locked_header:${name}`,
                  'hits_locked_header',
                  name + '-header',
                  { column: name },
                )
          }
          onClick={
            this.userIsPremium()
              ? _.noop
              : () =>
                  this.handleUpgradeClick(
                    name + '-header',
                    'hits_locked_header',
                    {
                      column: name,
                    },
                  )
          }
        >
          {this.premiumIcon({ name: name, popoverBodyText: popoverBodyText })}
          {title}
        </th>
      );
    };

    userIsPremium = () => {
      return Permissions.inMode('premium');
    };

    doorImpressionRef = (elementKey, door, trigger, extra = {}) => {
      if (!this.impressionRefs.has(elementKey)) {
        let currentElement = null;
        let watcher = null;
        this.impressionRefs.set(elementKey, (element) => {
          if (element === currentElement) return;
          watcher?.stop();
          watcher = null;
          currentElement = element;
          if (element) {
            watcher = watchAnalyticsImpression(
              element,
              'analytics-upgrade-door',
              door,
              { door, trigger, ...extra },
            );
          }
        });
      }
      return this.impressionRefs.get(elementKey);
    };

    trackUpgradeDoorClick = (door, trigger, extra = {}, entityId) => {
      trackAnalyticsClick(
        'analytics-upgrade-door',
        { door, trigger, ...extra },
        Number.isInteger(entityId) ? entityId : undefined,
      );
    };

    handleUpgradeClick = (
      trigger,
      door = 'hits_locked_header',
      extra = {},
      entityId,
    ) => {
      this.trackUpgradeDoorClick(door, trigger, extra, entityId);
      window.location = $r.upgrade_url({
        feature: 'analytics',
        trigger: trigger,
      });
    };
  },
);
